/**
 * Homepage subject router (`POST /chat/subjects/classify`) with a mock-mode
 * keyword shim. Application code imports from `src/api.ts`.
 */
import type { SubjectId } from '../data/subjects';
import { SUBJECTS } from '../data/subjects';
import { ApiError, CONTRACT_MESSAGE, USE_LIVE, fetchJson, validateOutgoingMessage } from './http';

interface ClassifySubjectResponse {
  subject: string;
}

const GENERIC_CLASSIFY_ERROR = 'Vi kunne ikke finde det rette fag lige nu. Prøv igen.';

function isKnownSubjectId(value: string): value is SubjectId {
  return SUBJECTS.some((subject) => subject.id === value);
}

/**
 * Keyword table for the mock-mode shim, in match order. Matematik is the
 * fallback and therefore has no entry.
 */
const MOCK_KEYWORDS: ReadonlyArray<readonly [SubjectId, readonly string[]]> = [
  [
    'fysik',
    [
      'fysik',
      'newton',
      'kraft',
      'energi',
      'hastighed',
      'acceleration',
      'elektricitet',
      'bølge',
      'atom',
      'tyngdekraft',
    ],
  ],
  ['kemi', ['kemi', 'mol', 'ph', 'syre', 'base', 'reaktion', 'stof', 'ion', 'organisk']],
  [
    'biologi',
    ['biologi', 'celle', 'dna', 'mitose', 'meiose', 'økologi', 'evolution', 'fotosyntese', 'gen'],
  ],
];

/**
 * Match a keyword at the start of a word, allowing Danish inflection to follow
 * (`Newtons`, `cellen`, `økologien`). Two details matter:
 *
 * - JavaScript's `\b` is defined over `[A-Za-z0-9_]`, so a `\b`-anchored pattern
 *   can never match a keyword beginning with `æ`, `ø`, or `å` — `\bøkologi`
 *   requires a word character at the boundary. Anchor on Unicode letters
 *   and digits instead.
 * - Only the leading boundary is anchored, so `ion` in `funktion` (mid-word)
 *   does not match while `kraften` (suffixed) does.
 */
function mentions(text: string, keyword: string): boolean {
  return new RegExp(`(?<![\\p{L}\\p{N}])${keyword}`, 'u').test(text);
}

/** Non-production dev shim for mock mode (D5). */
function classifySubjectQuestionMock(question: string): SubjectId {
  const normalized = question.toLowerCase();

  for (const [subject, keywords] of MOCK_KEYWORDS) {
    if (keywords.some((keyword) => mentions(normalized, keyword))) {
      return subject;
    }
  }
  return 'matematik';
}

export async function classifySubjectQuestion(question: string): Promise<SubjectId> {
  const trimmed = validateOutgoingMessage(question);

  if (!USE_LIVE) {
    return classifySubjectQuestionMock(trimmed);
  }

  let data: ClassifySubjectResponse;
  try {
    data = await fetchJson<ClassifySubjectResponse>('/chat/subjects/classify', {
      method: 'POST',
      session: true,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ question: trimmed }),
    });
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError({
      httpStatus: 503,
      code: null,
      userMessage: GENERIC_CLASSIFY_ERROR,
    });
  }

  if (!isKnownSubjectId(data.subject)) {
    throw new ApiError({
      httpStatus: 502,
      code: null,
      userMessage: CONTRACT_MESSAGE,
    });
  }

  return data.subject;
}
